import React, { useState } from 'react';
import { Check, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription, PricingPlan } from '../contexts/SubscriptionContext';
import { SubscriptionModal } from '../components/subscription/SubscriptionModal';
import { UsageTracker } from '../components/subscription/UsageTracker';
import styles from './UpgradePage.module.scss';

export const UpgradePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { pricingPlans, subscriptionStatus, createCheckoutSession } =
    useSubscription();

  // Debug logging
  console.log('UpgradePage - pricingPlans:', pricingPlans);
  console.log('UpgradePage - subscriptionStatus:', subscriptionStatus);

  // Fallback pricing plans in case API fails
  const fallbackPlans: PricingPlan[] = [
    {
      tier: 'basic',
      name: 'Basic',
      description: 'Perfect for getting started with AI learning',
      monthlyPrice: 9,
      yearlyPrice: 90,
      monthlyPriceId: 'price_basic_monthly',
      yearlyPriceId: 'price_basic_yearly',
      monthlyGenerations: 3,
      features: [
        '3 course generations per month',
        'Progress tracking',
        'Completion certificates',
        'Basic support',
      ],
    },
    {
      tier: 'pro',
      name: 'Pro',
      description: 'Advanced features for serious learners',
      monthlyPrice: 19,
      yearlyPrice: 190,
      monthlyPriceId: 'price_pro_monthly',
      yearlyPriceId: 'price_pro_yearly',
      monthlyGenerations: 10,
      features: [
        '10 course generations per month',
        'Priority generation queue',
        'Advanced analytics',
        'Custom difficulty levels',
        'Lesson export',
        '5 rollover generations',
        'Priority support',
      ],
    },
    {
      tier: 'unlimited',
      name: 'Unlimited',
      description: 'Everything you need for unlimited learning',
      monthlyPrice: 39,
      yearlyPrice: 390,
      monthlyPriceId: 'price_unlimited_monthly',
      yearlyPriceId: 'price_unlimited_yearly',
      monthlyGenerations: -1,
      features: [
        'Unlimited course generations',
        'White-label options',
        'API access',
        'Early access to new features',
        'Dedicated account manager',
        '24/7 priority support',
      ],
    },
  ];

  // Use fallback plans if API data is not available
  const displayPlans =
    pricingPlans && pricingPlans.length > 0 ? pricingPlans : fallbackPlans;
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>(
    'monthly'
  );
  const [selectedPlan, setSelectedPlan] = useState<PricingPlan | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSelectPlan = async (plan: PricingPlan) => {
    setLoading(true);
    try {
      const priceId =
        billingPeriod === 'yearly' ? plan.yearlyPriceId : plan.monthlyPriceId;
      const { url } = await createCheckoutSession(priceId, '/profile/billing');
      window.location.href = url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      // Fallback to modal
      setSelectedPlan(plan);
      setShowModal(true);
    } finally {
      setLoading(false);
    }
  };

  const getButtonText = (plan: PricingPlan) => {
    if (loading) return 'Loading...';

    const currentTier = subscriptionStatus?.subscriptionTier || 'free';

    if (currentTier === plan.tier) {
      return 'Current Plan';
    }

    // Determine if it's an upgrade or downgrade
    const tierOrder = { free: 0, basic: 1, pro: 2, unlimited: 3 };
    const currentOrder = tierOrder[currentTier as keyof typeof tierOrder] || 0;
    const planOrder = tierOrder[plan.tier];

    if (planOrder > currentOrder) {
      return 'Upgrade';
    } else {
      return 'Downgrade';
    }
  };

  const getButtonClass = (plan: PricingPlan) => {
    const currentTier = subscriptionStatus?.subscriptionTier || 'free';

    if (currentTier === plan.tier) {
      return `${styles.planButton} ${styles.disabled}`;
    }

    if (plan.tier === 'pro') {
      return `${styles.planButton} ${styles.primary}`;
    }

    return `${styles.planButton} ${styles.secondary}`;
  };

  const isCurrentPlan = (plan: PricingPlan) => {
    return subscriptionStatus?.subscriptionTier === plan.tier;
  };

  const getPrice = (plan: PricingPlan) => {
    return billingPeriod === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice;
  };

  const getOriginalPrice = (plan: PricingPlan) => {
    if (billingPeriod === 'yearly') {
      return plan.monthlyPrice * 12;
    }
    return null;
  };

  const getSavings = (plan: PricingPlan) => {
    const originalPrice = getOriginalPrice(plan);
    const currentPrice = getPrice(plan);
    return originalPrice ? originalPrice - currentPrice : 0;
  };

  return (
    <div className={styles.upgradePage}>
      <div className={styles.upgradeContainer}>
        {/* Header */}
        <div className={styles.pageHeader}>
          <button className={styles.backButton} onClick={() => navigate(-1)}>
            <ArrowLeft size={18} />
            Back
          </button>
          <h1>Choose Your Plan</h1>
          <p>Unlock unlimited learning with AI-powered course generation</p>
        </div>

        {/* Current Usage */}
        {subscriptionStatus && (
          <div className={styles.currentUsage}>
            <UsageTracker showUpgradeButton={false} />
          </div>
        )}

        {/* Billing Toggle */}
        <div className={styles.billingToggle}>
          <div className={styles.toggleContainer}>
            <button
              className={`${styles.toggleBtn} ${billingPeriod === 'monthly' ? styles.active : ''}`}
              onClick={() => setBillingPeriod('monthly')}
            >
              Monthly
            </button>
            <button
              className={`${styles.toggleBtn} ${billingPeriod === 'yearly' ? styles.active : ''}`}
              onClick={() => setBillingPeriod('yearly')}
            >
              Yearly
              <span className={styles.saveBadge}>Save 16%</span>
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className={styles.pricingCards}>
          {displayPlans?.map(plan => {
            const price = getPrice(plan);
            const originalPrice = getOriginalPrice(plan);
            const savings = getSavings(plan);
            const isCurrent = isCurrentPlan(plan);
            const isFeatured = plan.tier === 'pro';

            return (
              <div
                key={plan.tier}
                className={`${styles.pricingCard} ${isFeatured ? styles.featured : ''} ${isCurrent ? styles.current : ''}`}
              >
                {isFeatured && (
                  <div className={styles.popularBadge}>Most Popular</div>
                )}
                {isCurrent && (
                  <div className={styles.currentBadge}>Current Plan</div>
                )}

                <div className={styles.planHeader}>
                  <h3 className={styles.planName}>{plan.name}</h3>
                  <p className={styles.planDescription}>{plan.description}</p>
                </div>

                <div className={styles.planPricing}>
                  <div className={styles.priceDisplay}>
                    {billingPeriod === 'yearly' && savings > 0 && (
                      <span className={styles.originalPrice}>
                        ${originalPrice}
                      </span>
                    )}
                    <span className={styles.currency}>$</span>
                    <span className={styles.amount}>{price}</span>
                    <span className={styles.period}>
                      /{billingPeriod === 'yearly' ? 'year' : 'month'}
                    </span>
                  </div>
                  {billingPeriod === 'yearly' && savings > 0 && (
                    <p className={styles.billingNote}>
                      Save ${savings} per year
                    </p>
                  )}
                </div>

                <div className={styles.planFeatures}>
                  <ul className={styles.featuresList}>
                    {plan.features.map((feature, index) => (
                      <li key={index} className={styles.featureItem}>
                        <Check size={16} />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <button
                  className={getButtonClass(plan)}
                  onClick={() => handleSelectPlan(plan)}
                  disabled={isCurrent || loading}
                >
                  {getButtonText(plan)}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Subscription Modal */}
      <SubscriptionModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        selectedPlan={selectedPlan || undefined}
        isYearly={billingPeriod === 'yearly'}
      />
    </div>
  );
};
